import { useEffect, useState } from 'react';
import { get, put } from '../api';
import { ErrorBox, Modal, Spinner } from './base';

/**
 * Editing a module's per-product configuration.
 *
 * Until this existed the only way to set it was `wrangler d1 execute` against
 * production, which is not something a client could ever be handed - so the
 * module was not really sellable however tenant-scoped its code was.
 *
 * It is a JSON editor rather than a generated form, and that is a deliberate
 * trade rather than a shortcut. The settings are a nested, per-module,
 * adapter-dependent shape; a form covering every branch would be a great deal
 * of UI for an owner-only screen, and it would have to be rewritten for the
 * next module. What makes the editor worth having is not the widget, it is that
 * the server validates before storing and answers with a message per field -
 * so a typo is refused here, named, instead of degrading quietly weeks later.
 *
 * The presets exist for the same reason: a blank textarea and a schema in
 * somebody's head is how a config ends up subtly wrong.
 */

interface ProductModule {
  key: string;
  label: string;
  description: string;
  configurable: boolean;
  enabled: boolean;
  config: unknown;
  updated_at: string | null;
}

/** Starting points, so nobody has to remember the shape. */
const PRESETS: Record<string, { label: string; value: unknown }[]> = {
  'content-studio': [
    {
      label: 'Our own site (we approve, publishes via GitHub)',
      value: {
        languages: ['en', 'pt'],
        approval: ['internal'],
        publish: {
          adapter: 'github-actions',
          repo: 'Francisco0980/snip-work',
          workflow: 'publish.yml',
        },
      },
    },
    {
      label: 'Client site (we review, then they approve)',
      value: {
        languages: ['en'],
        approval: ['internal', 'client'],
        publish: { adapter: 'manual' },
      },
    },
    {
      label: 'Add a newsletter to the above',
      value: {
        newsletter: {
          enabled: true,
          name: 'Weekly',
          return_url: 'https://example.com/blog/',
        },
      },
    },
  ],
};

const pretty = (value: unknown) =>
  value === null || value === undefined ? '{\n}\n' : `${JSON.stringify(value, null, 2)}\n`;

export function ModuleConfigModal({ productId, moduleKey, onClose, onSaved }: {
  productId: string;
  moduleKey: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [module, setModule] = useState<ProductModule | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Server-reported problems, keyed by the path in the config they concern. */
  const [issues, setIssues] = useState<Record<string, string>>({});

  useEffect(() => {
    get<{ modules: ProductModule[] }>(`/products/${productId}/modules`)
      .then(({ modules }) => {
        const found = modules.find((m) => m.key === moduleKey) ?? null;
        setModule(found);
        setText(pretty(found?.config));
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load the settings.'));
  }, [productId, moduleKey]);

  const save = async () => {
    setBusy(true);
    setError(null);
    setIssues({});

    let parsed: unknown;
    try {
      parsed = text.trim() ? JSON.parse(text) : null;
    } catch (e) {
      // Caught here rather than sent, so a stray comma reads as "line 7" and
      // not as a 422 from the server about something else.
      setError(`That is not valid JSON: ${e instanceof Error ? e.message : 'parse failed'}`);
      setBusy(false);
      return;
    }

    try {
      await put(`/products/${productId}/modules`, {
        module: moduleKey,
        enabled: module?.enabled ?? true,
        config: parsed,
      });
      onSaved();
      onClose();
    } catch (e) {
      const api = e as { message?: string; details?: Record<string, string> };
      setError(api.message ?? 'Could not save.');
      if (api.details) setIssues(api.details);
    } finally {
      setBusy(false);
    }
  };

  const presets = PRESETS[moduleKey] ?? [];

  return (
    <Modal open onClose={onClose} title={`${module?.label ?? 'Module'} settings`} width="max-w-3xl">
      {error && <ErrorBox message={error} />}

      {Object.keys(issues).length > 0 && (
        <div className="mb-3 rounded-lg bg-red-50 p-3 text-sm dark:bg-red-950/40">
          <p className="mb-1.5 font-medium text-red-900 dark:text-red-200">
            Nothing was saved. Fix these and try again:
          </p>
          <ul className="space-y-1">
            {Object.entries(issues).map(([path, message]) => (
              <li key={path} className="text-red-900 dark:text-red-200">
                <code className="font-mono text-xs">{path}</code> {message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!module ? <Spinner /> : (
        <div className="space-y-3">
          <p className="text-sm text-ink-600 dark:text-paper-300">{module.description}</p>

          {presets.length > 0 && (
            <div>
              <span className="form-label">Start from</span>
              <div className="flex flex-wrap gap-2">
                {presets.map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    className="btn-secondary text-xs"
                    onClick={() => {
                      // Merged into what is there rather than replacing it, so
                      // "add a newsletter" does not silently drop the
                      // publishing target the product was already using.
                      let current: Record<string, unknown> = {};
                      try { current = text.trim() ? JSON.parse(text) : {}; } catch { current = {}; }
                      setText(pretty({ ...current, ...(preset.value as object) }));
                    }}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="form-label" htmlFor="module-config">Settings (JSON)</label>
            <textarea
              id="module-config"
              className="field min-h-[280px] font-mono text-xs"
              spellCheck={false}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
              Checked before it is stored - an unknown setting, a stage that does not exist, or a
              publishing target missing what it needs is refused here rather than failing later.
              The previous settings are kept in the audit trail.
            </p>
          </div>

          <div className="flex gap-2">
            <button type="button" className="btn-primary" disabled={busy} onClick={save}>
              {busy && <Spinner className="h-4 w-4" />} Save settings
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
